import React from 'react';
import { TextField } from '@mui/material';

export const TOTP_CODE_LENGTH = 6;

/**
 * Whether a code is ready to submit. Exported so every caller gates its submit
 * button on one predicate rather than each writing the regex — there are three
 * call sites (login challenge, enrollment, turning MFA off) and three copies of
 * `/^\d{6}$/` is three chances to disagree.
 */
export const isCompleteCode = (value) => new RegExp(`^\\d{${TOTP_CODE_LENGTH}}$`).test(value);

// Authenticator apps display codes grouped ("123 456"), and people paste what
// they see. Stripping here rather than at each call site means a paste is
// accepted everywhere instead of silently failing validation in one place.
//
// The slice is also what bounds the field to six digits, and it has to be —
// a `maxLength` of six on the input counts separators as characters, so the
// browser truncates the paste to "123 45" before any of this runs and the user
// is left with five digits, a disabled submit, and nothing on screen saying
// why. Clamping after the strip bounds typing exactly as well (the value is
// controlled, so a seventh digit round-trips back to six) without deciding the
// length of a paste by counting the spaces in it.
const digitsOnly = (value) => value.replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH);

/**
 * The six-digit TOTP input, shared by the login challenge and the account
 * screen's MFA dialogs.
 *
 * It is a text field and nothing more. Deliberately absent:
 *
 *  - **No form semantics and no Enter handling.** One caller renders it inside
 *    a `<Box component="form">` where Enter already submits natively, another
 *    inside a dialog. An internal key handler would double-fire in the first.
 *    Submit belongs to the parent.
 *  - **No auto-submit when the sixth digit lands.** `autoComplete="one-time-code"`
 *    populates programmatically, so auto-submit would fire on the autofill and
 *    take away the user's chance to see what was filled in before it is spent —
 *    and a mistyped paste would burn a rate-limit attempt against a 429 that
 *    carries no Retry-After.
 *  - **No error state of its own.** Both callers keep the inline error in the
 *    step or dialog that owns the request, specifically so a rejected code
 *    cannot reach a page-level error and tear down the surrounding flow. That
 *    only holds while this component stays stateless about errors.
 *
 * `errorText` rather than MUI's boolean `error`: the component composes
 * error-over-helper precedence once, instead of three call sites each writing
 * `codeError || countdownText` and each being a chance to get the order
 * backwards. It is not named `error` because MUI's own prop of that name takes
 * a boolean, and shadowing it with a string invites `error={Boolean(x)}` from
 * habit — which would render the string "true" as helper text.
 *
 * `helperText` stays a real, separate slot because both callers put a countdown
 * in it: the login challenge's five-minute window, and the enrollment setup
 * session's.
 * On the enrollment side that countdown is load-bearing rather than decorative —
 * an expired session and a mistyped code both come back as 403 INVALID_CODE and
 * the server cannot tell them apart, so the clock is the only thing that can.
 */
function TotpCodeField({
  id,
  label = 'Verification code',
  value,
  onChange,
  errorText,
  helperText,
  disabled,
  autoFocus,
}) {
  return (
    <TextField
      id={id}
      name={id}
      label={label}
      value={value}
      onChange={(event) => onChange(digitsOnly(event.target.value))}
      error={Boolean(errorText)}
      helperText={errorText || helperText}
      disabled={disabled}
      autoFocus={autoFocus}
      fullWidth
      slotProps={{
        htmlInput: {
          inputMode: 'numeric',
          autoComplete: 'one-time-code',
          pattern: '[0-9]*',
        },
        // The countdown and the error share one slot, so the live region is
        // switched on only while it holds an error — marking it assertive
        // unconditionally would have a screen reader read the clock aloud once
        // a second. It has to be a live region at all because by the time an
        // error appears focus is on the submit button, and helper text reaches
        // the user through aria-describedby, which is only read when the field
        // it describes is focused. Without this the user presses Verify and
        // hears nothing whatsoever.
        formHelperText: errorText ? { role: 'alert' } : undefined,
      }}
    />
  );
}

export default TotpCodeField;
